import React from "react";
import { Box, Container, Typography, styled } from "@mui/material";

interface BannerWithContentProps {
  backgroundImage: string;
  isOverlap?: boolean;
  children?: React.ReactNode;
  title?: string;
  description?: string;
  overlay?: boolean;
}

const BannerWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  minHeight: "220px",
  aspectRatio: "8 / 3",
  maxHeight: "85vh",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  textAlign: "center",
}));

const BannerOverlay = styled(Box)(({ theme }) => ({
  backgroundColor: "#290B41C2",
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "15px",
  [theme.breakpoints.down("sm")]: {
    gap: "10px",
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  color: "#fff",
  textAlign: "center",
  fontWeight: 600,
  margin: "0 4rem",
  fontSize: "clamp(1.4rem, 5vw, 4.7rem)",
  lineHeight: "clamp(1.8rem, 5vw, 4.8rem)",
  [theme.breakpoints.down("md")]: {
    margin: "0 3rem",
  },
  [theme.breakpoints.down("sm")]: {
    margin: "0 1rem",
  },
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
  color: "#fff",
  textAlign: "center",
  fontWeight: 500,
  fontSize: "clamp(0.9rem, 2vw, 1.125rem)",
  lineHeight: "clamp(1.1rem, 2.5vw, 1.75rem)",
  [theme.breakpoints.down("md")]: {
    margin: "0 2rem",
  },
  [theme.breakpoints.down("sm")]: {
    margin: "0 1rem",
  },
}));

const OverlapWrapper = styled(Box)(({ theme }) => ({
  padding: "50px",
  backgroundColor: "#eff6ff",
  display: "flex",
  justifyContent: "center",
  [theme.breakpoints.down("lg")]: {
    padding: "40px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "35px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "25px",
  },
  [theme.breakpoints.down("xs")]: {
    padding: "15px",
  },
}));

const OverlapContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOverlap",
})<{ isOverlap?: boolean }>(({ theme, isOverlap }) => ({
  zIndex: 9,
  width: "100%",
  position: "relative",
  marginTop: isOverlap ? "-20%" : 0,
  borderRadius: isOverlap ? "20px" : 0,
  backgroundColor: isOverlap ? "#fff" : "transparent",
  maxWidth: isOverlap ? "1280px" : "auto",
  padding: isOverlap ? "60px" : "0px",
  [theme.breakpoints.down("lg")]: {
    padding: isOverlap ? "45px 40px 40px": "0px",
  },
  [theme.breakpoints.down("md")]: {
    padding: isOverlap ? "40px 35px 35px": "0px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: isOverlap ? "30px 25px 25px": "0px",
  },
  [theme.breakpoints.down("xs")]: {
    padding: isOverlap ? "25px 20px 20px": "0px",
  },
}));

const BannerWithContent: React.FC<BannerWithContentProps> = ({
  backgroundImage,
  isOverlap = false,
  children,
  title,
  description,
  overlay = false,
}) => {
  return (
    <Box bgcolor="#eff6ff">
      <BannerWrapper sx={{ backgroundImage: `url(${backgroundImage})` }}>
        {overlay && (
          <BannerOverlay>
            {title && <TitleText>{title}</TitleText>}
            {description && <DescriptionText>{description}</DescriptionText>}
          </BannerOverlay>
        )}
      </BannerWrapper>

      <OverlapWrapper>
        <OverlapContainer isOverlap={isOverlap}>{children}</OverlapContainer>
      </OverlapWrapper>
    </Box>
  );
};

export default BannerWithContent;
